import { FC, SVGProps, useId } from 'react';

import { ScaleColor } from '@scaleaq/scaleui/utility/types';

import type { TemplateWidget } from '@/constants';
import { colors } from '@/utils';

import {
  GAP,
  getTemplateIconGeometry,
  VIEWBOX_WIDTH,
  type TemplateIconExtent,
} from './templateIcon/getTemplateIconGeometry.ts';
import {
  TEMPLATE_ICON_APPEARANCES,
  type TemplateIconAppearance,
  type TemplateIconPalette,
} from './templateIcon/templateIcon.constants.ts';

const BORDER_RADIUS = 1.5;
const INDEX_FONT_SIZE = 5.4;

/**
 * Generic SVG icon generator for layout templates
 * Automatically generates an icon based on the template's widget structure
 */
export const TemplateIcon: FC<
  SVGProps<SVGSVGElement> & {
    widgets: TemplateWidget[];
    withIndex?: boolean;
    strokeColor?: ScaleColor | undefined;
    /**
     * Cell stroke width in viewBox units, scaling with the icon. Without it, cells get a crisp
     * 1px line at any size; a small value gives the softer, sub-pixel line of tiny menu icons.
     */
    scalingStrokeWidth?: number;
    /** Draw against this grid instead of the widgets' bounding box, so empty space shows. */
    extent?: TemplateIconExtent | undefined;
    /** Show only what fits inside `extent`, cutting off widgets that reach past it. */
    clipToExtent?: boolean;
    appearance?: TemplateIconAppearance;
  }
> = ({
  widgets,
  withIndex,
  strokeColor,
  scalingStrokeWidth,
  extent,
  clipToExtent = false,
  appearance = 'default',
  style,
  ...restProps
}) => {
  const clipPathId = useId();
  const palette: TemplateIconPalette = TEMPLATE_ICON_APPEARANCES[appearance];
  const { unitWidth, unitHeight, viewBoxHeight, frameWidth, frameHeight, overflows } =
    getTemplateIconGeometry({ widgets, extent, clipToExtent });
  const frameColor = overflows ? palette.overflowFrame : palette.frame;

  // Sort widgets by y then x for consistent rendering
  const sortedWidgets = [...widgets].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='100%'
      height={widgets.length === 0 ? '100%' : '50%'}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${viewBoxHeight}`}
      fill='none'
      style={{
        color: colors.background.info.solid[100],
        // The non-scaling frame stroke straddles the viewBox edge; keep its outer half visible.
        overflow: frameColor ? 'visible' : undefined,
        ...style,
      }}
      {...restProps}
    >
      <clipPath id={clipPathId}>
        <rect width={VIEWBOX_WIDTH} height={viewBoxHeight} />
      </clipPath>
      <g clipPath={`url(#${clipPathId})`}>
        {sortedWidgets.map((widget, index) => {
          const x = widget.x * unitWidth + GAP;
          const y = widget.y * unitHeight + GAP;
          const width = Math.max(0, widget.w * unitWidth - GAP);
          const height = Math.max(0, widget.h * unitHeight - GAP);
          return (
            <g key={`${widget.x}-${widget.y}-${widget.w}-${widget.h}-${index}`}>
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                rx={BORDER_RADIUS}
                fill={palette.fill}
                fillOpacity={palette.fillOpacity}
                stroke={strokeColor || palette.stroke}
                strokeWidth={scalingStrokeWidth ?? 1}
                vectorEffect={scalingStrokeWidth === undefined ? 'non-scaling-stroke' : undefined}
              />
              {withIndex && (
                <text
                  x={x + width / 2}
                  y={y + height / 2}
                  textAnchor='middle'
                  dominantBaseline='central'
                  fill='currentColor'
                  fontSize={INDEX_FONT_SIZE}
                  fontWeight={600}
                >
                  {index + 1}
                </text>
              )}
            </g>
          );
        })}
      </g>
      {frameColor && (
        <rect
          x={0}
          y={0}
          width={frameWidth}
          height={frameHeight}
          rx={BORDER_RADIUS * 2}
          stroke={frameColor}
          strokeWidth={1}
          vectorEffect='non-scaling-stroke'
        />
      )}
    </svg>
  );
};
